import { useTranslation } from "react-i18next";
import { DocumentCraftingUser } from "../../../Models/documentCrafting";

// Server timestamps: the Ambassador helpers parse both the naive
// "YYYY-MM-DD HH:MM:SS.ffffff" and the ISO "YYYY-MM-DDTHH:MM:SS" form.
export {
  parseDate,
  formatDate,
  formatDateTime,
} from "../../Ambassador/Common/format";

/** "Name Surname"; "" for a deleted user (use `useUserName` to label it). */
export const displayName = (user: DocumentCraftingUser | null | undefined) =>
  user ? `${user.name} ${user.surname}`.trim() || user.email : "";

/** A user's name, or the translated "Deleted user" for null. */
export const useUserName = () => {
  const { t } = useTranslation("documentCrafting");
  return (user: DocumentCraftingUser | null | undefined) =>
    user ? displayName(user) : t("common.deletedUser");
};

/** AI cost in USD: two decimals, more for small amounts ("$0.0042"). */
export const formatCost = (value: number | null | undefined, language: string) =>
  (value ?? 0).toLocaleString(language, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(value ?? 0) < 1 && value ? 4 : 2,
  });

export const formatFileSize = (bytes: number, language: string) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toLocaleString(language, { maximumFractionDigits: 1 })} ${units[unit]}`;
};

/** Download name `<title>_r<N>.<ext>`, as the backend names it (CORS hides
 * its Content-Disposition, so the frontend passes the full name itself). */
export const downloadFileName = (
  title: string,
  revisionNumber: number | null,
  ext: string,
  suffix?: string,
) => {
  const printable = title
    .split("")
    .filter((char) => char.charCodeAt(0) >= 32)
    .join("");
  const safeTitle =
    printable
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120) || "document";
  const revision = revisionNumber ? `_r${revisionNumber}` : "";
  return `${safeTitle}${revision}${suffix ? `_${suffix}` : ""}.${ext}`;
};
